import Text from '@atoms/Text/Text';
import {
  ColourSwatchCardBG,
  IColourSwatchCard,
} from '@models/IColourSwatchCard';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import ColourSwatchVariantEnum from '@models/enums/ColourSwatchVariantEnum';
import SiteEnum from '@models/enums/SiteEnum';
import ColourSwatchGallery from '@molecules/ColourSwatchGallery/ColourSwatchGallery';
import { WishlistButtonContext } from '@utility/context/WishlistButtonContext';
import useIsTouchDevice from '@utility/hooks/useIsTouchDevice';
import classNames from 'classnames';
import { useInView } from 'motion/react';
import {
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

interface Props extends IColourSwatchCard {
  readonly createColourImage?: boolean;
  readonly showLikeButton?: boolean;
  readonly wishlistId?: number;
  readonly onClick?: (
    e?: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLElement>
  ) => void;
  readonly site?: SiteEnum;
  readonly triggerRef?: RefObject<HTMLDivElement | null>;
}

const ClickWrapper = ({
  link,
  onClick,
  ref,
  children,
  className,
}: PropsWithChildren<{
  link?: string;
  onClick?: (
    e?: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLElement>
  ) => void;
  ref?: RefObject<HTMLDivElement | null>;
  className: string;
}>) => {
  return link ? (
    <a href={link} className={className}>
      {children}
    </a>
  ) : (
    <div
      role="button"
      onClick={onClick}
      ref={ref}
      className={className}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onClick) onClick(e);
      }}
    >
      {children}
    </div>
  );
};

const ColourSwatchCard = (props: Props) => {
  const {
    id,
    bgColour = ColourSwatchCardBG.Grey,
    colourCode,
    colourHexCode,
    colourName,
    description,
    link,
    showLikeButton = true,
    variant = ColourSwatchVariantEnum.Default,
    wishlistDrawerLabels,
    wishlistId,
    onClick,
    site,
    triggerRef,
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [disableHover, setDisableHover] = useState(false);
  const isTouchDevice = useIsTouchDevice();
  const isDefaultVariant = variant === ColourSwatchVariantEnum.Default;
  const isDetailedVariant = variant === ColourSwatchVariantEnum.Detailed;
  const isSimpleVariant = variant === ColourSwatchVariantEnum.Simple;
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { amount: 0, once: true });
  const WishlistButton = useContext(WishlistButtonContext);

  const handleTrackingEvent = useCallback(() => {
    window.dataLayer.push({
      event: 'trackingEvent_select_item',
      event_name_variable: 'select_item',
      trackingEvent_select_item: {
        item_name: colourName,
        item_category: 'colour',
        item_category3: colourHexCode,
      },
    });
  }, [colourName, colourHexCode]);

  const handleHover = (isHovered: boolean) => {
    if (disableHover) return;
    setIsHovered(isHovered);
  };

  return (
    <div
      className="tw-group tw-relative tw-flex tw-h-full tw-flex-col"
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
      onFocus={() => handleHover(true)}
      onBlur={() => handleHover(false)}
      ref={containerRef}
    >
      {showLikeButton && WishlistButton && wishlistDrawerLabels && (
        <WishlistButton
          wishlistId={wishlistId}
          card={props}
          cardId={id}
          collection={CardTypeEnum.Colours}
          wishlistDrawerLabels={wishlistDrawerLabels}
          onDrawerStateChange={(isOpen) => setDisableHover(isOpen)}
        />
      )}
      <ClickWrapper
        link={link}
        onClick={(e) => {
          onClick?.(e);
          if (site === SiteEnum.B2B) return;
          handleTrackingEvent();
        }}
        ref={triggerRef}
        className="tw-relative tw-flex tw-h-full tw-flex-col tw-text-left"
      >
        <ColourSwatchGallery {...props} inView={inView} isHovered={isHovered} />
        {variant !== ColourSwatchVariantEnum.Large && (
          <div
            className={classNames(
              'tw-flex tw-w-full tw-flex-grow tw-flex-col tw-gap-1 tw-text-grey-100 tw-transition-colors dark:tw-bg-white',
              bgColour,
              isSimpleVariant ? 'tw-px-2.5 tw-py-xxs' : 'tw-p-xs'
            )}
          >
            <Text
              className="tw-block tw-text-grey-70"
              variant="label-12"
              as="p"
            >
              {colourCode}
            </Text>

            {!isSimpleVariant && (
              <Text
                variant={
                  isTouchDevice && isDefaultVariant
                    ? 'body-18'
                    : 'heading-21-light'
                }
                className="tw-capitalize"
              >
                {colourName?.toLowerCase()}
              </Text>
            )}

            {isDetailedVariant && description && (
              <Text
                className="tw-ellipsis tw-mt-4 tw-line-clamp-3 tw-text-grey-70"
                variant="body-small"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>
        )}
      </ClickWrapper>
    </div>
  );
};

export default ColourSwatchCard;
